import React, { Fragment, useState } from 'react';
import { useAsync } from 'react-async';
import { getMessages, createMessage } from '../../api/services/messageService';
import { getRecipients } from '../../api/services/userService';
import Layout from '../../components/layout';
import { useLocation, useHistory } from 'react-router-dom';
import { Notification } from '../../components/notifications';
import LayoutError from '../../components/layoutError';
import moment from 'moment';
import DataTable from '../../components/dataTable';
import { EyeIcon } from '@heroicons/react/solid';
import { ExclamationCircleIcon } from '@heroicons/react/solid';
import MessageDisplay from '../../api/models/messageDisplay';
import Modal from '../../components/modal';
import { FieldInput } from '../../components/fieldInput';
import { ResponseStatus } from '../../components/notifications';
import { useDispatch } from 'react-redux';
import { updateUnreadMessagesCount } from '../../slices/userSlice';
import DOMPurify from 'dompurify';
import Select from 'react-select';
import { graySelectStyles } from '../../common/selectStyles';

type ViewState = {
  message?: string;
};

const fetchUsers = async () => {
  return await getRecipients(true);
};

const ViewMessages = () => {
  const columns = React.useMemo(
    () => [
      {
        Header: 'From',
        accessor: (m: any) =>
          m.isMyMessage
            ? 'Me'
            : m.fromUserFirstName + ', ' + m.fromUserLastName,

        extraAccessor: 'fromUserRoles',
        basicStyle: 'isRead',
        Cell: ({ value, column, row }: any) => {
          return (
            <div className='flex items-top'>
              {!row.original[column.basicStyle] && !row.original['isMyMessage'] && (
                <div className='flex-shrink-0 w-5 h-5'>
                  <ExclamationCircleIcon
                    className='w-5 h-5 cursor-pointer'
                    aria-hidden='true'
                    color='red'
                  />
                </div>
              )}
              <div className={row.original[column.basicStyle] ? 'ml-4' : ''}>
                <div>{value}</div>
                <div className='text-sm text-gray-500'>
                  {row.original[column.extraAccessor]}
                </div>
              </div>
            </div>
          );
        },
        width: 100,
      },
      {
        Header: 'Date/Time EST',
        accessor: (m: any) => moment(m.createdOn).format('MM/DD/YYYY @h:mmA'),
        width: 50,
      },
      {
        Header: 'Thread Topic',
        accessor: 'threadTopic',
        className: 'hidden md:table-cell ',
        width: 100,
      },
      {
        Header: 'Message',
        accessor: 'messageText',
        Cell: ({ value, column, row }: any) => <div dangerouslySetInnerHTML={{ __html: row.original.messageText }}></div>
      },
      {
        accessor: 'threadId',
        disableSortBy: true,
        Cell: () => {
          return (
            <div className='clearfix'>
              <span className='float-right'>
                <EyeIcon
                  className='w-5 h-5 cursor-pointer'
                  aria-hidden='true'
                />
              </span>
            </div>
          );
        },
        width: 50,
        className: 'hidden sm:table-cell',
      },
    ],
    []
  );

  const history = useHistory();
  const { state } = useLocation<ViewState>();
  const [messages, setMessages] = useState<MessageDisplay[]>([]);
  const [openMessage, setOpenMessage] = useState(false);
  const [topic, setTopic] = useState('');
  const [message, setMessage] = useState('');
  const [recipient, setRecipient] = useState(0);
  const dispatch = useDispatch();

  const { error, isPending } = useAsync({
    promiseFn: getMessages,
    onResolve: (r) => {
      const sanitizedMessages = r.data.messages.map(message => ({
        ...message,
        messageText: DOMPurify.sanitize(message.messageText)
      }));

      setMessages(sanitizedMessages);
      let unreadMessages = sanitizedMessages.filter(x => !x.isRead && !x.isMyMessage)?.length;
      dispatch(updateUnreadMessagesCount(unreadMessages ?? 0))
    },
  });

  const usersData = useAsync({
    promiseFn: fetchUsers,
  });

  const createNewMessage = async () => {
    const sanitizedMessage = DOMPurify.sanitize(message);

    return await createMessage({
      toUserId: recipient,
      messageText: sanitizedMessage,
      threadTopic: topic,
    });
  };

  const addMessage = useAsync({
    deferFn: createNewMessage,
    onResolve: (r) => {
      setMessages(r.data.messages);
    },
  });

  const handleCreate = () => {
    addMessage.run();
  };

  if (error) return <LayoutError />;

  return (
    <Fragment>
      <Modal
        saveText='Send'
        allowSave={
          topic.length !== 0 && message.length !== 0 && recipient !== 0
        }
        open={openMessage}
        onCancel={() => {
          setOpenMessage(false);
        }}
        onSave={() => {
          handleCreate();
          setOpenMessage(false);
          setMessage('');
          setTopic('');
          setRecipient(0);
        }}
      >
        <Fragment>
          <div className='grid grid-cols-6 gap-6 mb-6'>
            <div className='col-span-6'>
              <label className='primary'>
                Recipient <span className='pl-1 text-red-500'>*</span>
              </label>
              <Select
                options={usersData.data?.data.users
                  .filter(u => !u.deletedOn)
                  .sort((a, b) => a.lastName.localeCompare(b.lastName))
                  .map((u) => ({
                    value: u.userId,
                    label: u.lastName + ', ' + u.firstName,
                  })) ?? []}
                value={
                  (usersData.data?.data.users
                    .filter(u => !u.deletedOn)
                    .map((u) => ({
                      value: u.userId,
                      label: u.lastName + ', ' + u.firstName,
                    })) ?? []
                  ).find((o) => o.value === recipient) || null
                }
                onChange={(selected) => setRecipient(selected ? Number(selected.value) : 0)}
                placeholder=' -- Choose Recipient --'
                styles={graySelectStyles}
                isSearchable={false}
                className='block'
              />
            </div>
            <div className='col-span-6'>
              <FieldInput
                name='topic'
                label='Topic'
                minLength={1}
                maxLength={100}
                onChange={(e) => setTopic(e.target.value)}
                value={topic}
              />
            </div>
            <div className='col-span-6'>
              <label className='primary'>
                Message
                <span className='pl-1 text-red-500'>*</span>
              </label>
              <textarea
                minLength={1}
                maxLength={500}
                className='block mt-1 primary'
                value={message}
                rows={3}
                onChange={(e) => setMessage(e.target.value)}
              />
            </div>
          </div>
        </Fragment>
      </Modal>
      <Layout isLoading={isPending}>
        <Fragment>
          {addMessage.error && <ResponseStatus state={addMessage} />}
          {state?.message && <Notification>{state?.message}</Notification>}
          <div className='text-right'>
            <button
              onClick={() => {
                setOpenMessage(true);
              }}
            >
              New Message
            </button>
          </div>
          <DataTable
            columns={columns}
            data={messages}
            showPagination={messages.length > 30 ? true : false}
            getRowProps={(row: any) => ({
              className: row.original.isMyMessage
                ? 'cursor-pointer hover:bg-purple-300 bg-purple-200'
                : row.original.isRead
                  ? 'cursor-pointer hover:bg-gray-100'
                  : 'cursor-pointer hover:bg-gray-100 font-bold',
              onClick: () => history.push(`/messages/${row.values.threadId}`),
            })}
          />
        </Fragment>
      </Layout>
    </Fragment>
  );
};

export default ViewMessages;
